import axios, { AxiosResponse } from 'axios';
import { useRef } from 'react';

import CONFIG from '@/config/config';

import { HTTP_STATUS } from './ErrorCode.model';
import { HTTP_HEADER } from './httpHeaders.model';
import { HttpMethod } from './HttpMethod.model';

export const getCsrfToken = async () => {
  const { data } = await axios.get<{ token: string }>(CONFIG.HOST + '/api/v1/csrf', {
    headers:
      CONFIG.ENVIRONMENT === 'local'
        ? {
            'x-auth-token': `${CONFIG.JSESSION}`,
          }
        : {},
  });
  return data.token;
};

const successfulResponseHandler = (response: AxiosResponse): AxiosResponse => {
  if (response.status === HTTP_STATUS.REDIRECT) {
    delete response.headers[HTTP_HEADER['X-CSRF-TOKEN']];
  }

  return response;
};

type Return = ReturnType<typeof axios.create>;

export const useAxiosInstance = (): Return => {
  const isErrorInterceptorAddedRef = useRef(false);

  const axiosInstance = axios.create();

  if (!isErrorInterceptorAddedRef.current) {
    isErrorInterceptorAddedRef.current = true;
    axiosInstance.interceptors.response.use(successfulResponseHandler);
  }

  axiosInstance.interceptors.request.use(
    async config => {
      if (
        config.method &&
        [HttpMethod.PUT, HttpMethod.POST, HttpMethod.PATCH, HttpMethod.DELETE].some(
          method => method === config.method,
        )
      ) {
        config.headers[HTTP_HEADER['X-CSRF-TOKEN']] = await getCsrfToken();
      } else {
        delete config.headers[HTTP_HEADER['X-CSRF-TOKEN']];
      }
      if (CONFIG.ENVIRONMENT === 'local') {
        config.headers['x-auth-token'] = `${CONFIG.JSESSION}`;
      }
      return config;
    },
    error => Promise.reject(error),
  );

  return axiosInstance;
};
